import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Button,
  Divider,
  Link,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircleOutline as StepIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

const TEXT_PRIMARY = '#E6EDF7';
const TEXT_MUTED = 'rgba(230, 237, 247, 0.72)';
const CARD_BG = '#111A2C';
const CARD_BORDER = '1px solid rgba(148, 163, 184, 0.22)';
const INNER_WELL = 'rgba(22, 35, 58, 0.95)';

const PRIORITY_CHIP = {
  high: { bgcolor: 'rgba(239, 68, 68, 0.2)', color: '#F87171', border: '1px solid rgba(239, 68, 68, 0.35)' },
  medium: { bgcolor: 'rgba(245, 158, 11, 0.18)', color: '#FBBF24', border: '1px solid rgba(245, 158, 11, 0.35)' },
  low: { bgcolor: 'rgba(34, 197, 94, 0.16)', color: '#4ADE80', border: '1px solid rgba(34, 197, 94, 0.3)' },
};

const PRIORITY_LABELS = {
  high: 'High Priority',
  medium: 'Medium Priority',
  low: 'Low Priority',
};

/**
 * Displays a single grouped security recommendation with steps
 * and an expandable list of affected posts/comments.
 */
const RecommendationCard = ({ recommendation }) => {
  const [showExposures, setShowExposures] = useState(false);

  if (!recommendation) return null;

  const { title, priority, exposure_count, steps, exposures } = recommendation;
  const chipSx = PRIORITY_CHIP[priority] || PRIORITY_CHIP.low;
  const priorityLabel = PRIORITY_LABELS[priority] || priority;

  return (
    <Card
      elevation={0}
      sx={{
        mb: 2.5,
        borderRadius: 3,
        bgcolor: CARD_BG,
        border: CARD_BORDER,
        boxShadow: '0 10px 26px rgba(2, 6, 23, 0.45)',
        transition: 'box-shadow 0.22s ease, transform 0.22s ease',
        '&:hover': {
          boxShadow: '0 14px 34px rgba(2, 6, 23, 0.55)',
        },
      }}
    >
      <CardContent sx={{ py: 2.75, px: 2.75 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              flex: 1,
              color: TEXT_PRIMARY,
              fontSize: '1.125rem',
              lineHeight: 1.4,
              letterSpacing: '-0.01em',
            }}
          >
            {title}
          </Typography>
          <Chip
            label={priorityLabel}
            size="small"
            sx={{ fontWeight: 700, flexShrink: 0, ...chipSx }}
          />
        </Box>

        <Typography variant="body2" sx={{ mb: 2, color: TEXT_MUTED, fontSize: '0.9375rem' }}>
          {exposure_count} unresolved {exposure_count === 1 ? 'exposure' : 'exposures'} found
        </Typography>

        <Divider sx={{ borderColor: 'rgba(148, 163, 184, 0.18)', mb: 2 }} />

        <Typography
          variant="overline"
          sx={{ mb: 1.25, fontWeight: 800, letterSpacing: '0.1em', color: TEXT_PRIMARY, display: 'block' }}
        >
          What to do
        </Typography>
        <List dense disablePadding>
          {steps && steps.map((step, index) => (
            <ListItem key={index} alignItems="flex-start" disablePadding sx={{ mb: 1 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.35 }}>
                <StepIcon sx={{ fontSize: 22, color: '#18B89A' }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ color: TEXT_PRIMARY, lineHeight: 1.65, fontSize: '0.9375rem' }}>
                    <Box component="span" sx={{ fontWeight: 700, color: '#40D2B7', mr: 0.5 }}>
                      {index + 1}.
                    </Box>
                    {step}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      </CardContent>

      {exposures && exposures.length > 0 && (
        <CardActions sx={{ px: 2.75, pb: 1.5, pt: 0 }}>
          <Button
            size="small"
            onClick={() => setShowExposures(!showExposures)}
            endIcon={showExposures ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              color: '#40D2B7',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': { bgcolor: 'rgba(24, 184, 154, 0.1)', color: '#5EEAD4' },
            }}
          >
            {showExposures ? 'Hide' : 'Show'} affected posts ({exposures.length})
          </Button>
        </CardActions>
      )}

      <Collapse in={showExposures} unmountOnExit>
        <Divider sx={{ borderColor: 'rgba(148, 163, 184, 0.18)' }} />
        <Box sx={{ px: 2.75, py: 2, bgcolor: INNER_WELL }}>
          <List dense disablePadding>
            {exposures && exposures.map((exp, index) => (
              <ListItem key={index} disablePadding sx={{ mb: 1.5 }}>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        wordBreak: 'break-word',
                        color: TEXT_PRIMARY,
                        fontSize: '0.875rem',
                      }}
                    >
                      {exp.content || 'No content available'}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 0.75 }}>
                      <Chip
                        label={exp.source || 'unknown'}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          color: TEXT_MUTED,
                          borderColor: 'rgba(148, 163, 184, 0.35)',
                        }}
                      />
                      {exp.subreddit && (
                        <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
                          r/{exp.subreddit}
                        </Typography>
                      )}
                      {exp.url && (
                        <Link
                          href={exp.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="caption"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.3,
                            color: '#40D2B7',
                            fontWeight: 600,
                            '&:hover': { color: '#5EEAD4' },
                          }}
                        >
                          View post <OpenInNewIcon sx={{ fontSize: 12 }} />
                        </Link>
                      )}
                    </Box>
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Collapse>
    </Card>
  );
};

export default RecommendationCard;
